/**
 * Safely parse structured JSON from AI generate responses.
 * Handles markdown fences, multiline HTML in string values, and common AI formatting issues.
 */

const USABLE_KEY_PATTERNS = [
  /^shortdesc$/i,
  /^fulldesc$/i,
  /^pagetitle$/i,
  /^metadesc$/i,
  /^metakeys$/i,
  /^readingtime$/i,
  /^sectiondescription$/i,
  /^subsectiondescription$/i,
];

const LOG_PREFIX = "[AI Response Parser]";

const truncateForLog = (value, max = 4000) => {
  if (value === null || value === undefined) return value;
  const str = typeof value === "string" ? value : JSON.stringify(value);
  if (str.length <= max) return str;
  return `${str.slice(0, max)}… [truncated ${str.length - max} chars]`;
};

/**
 * @param {unknown} obj
 * @returns {boolean}
 */
export const hasUsableContentKeys = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  return Object.keys(obj).some((key) => {
    if (!USABLE_KEY_PATTERNS.some((pattern) => pattern.test(key))) return false;
    const value = obj[key];
    if (value === null || value === undefined) return false;
    if (typeof value === "object") return false;
    return String(value).trim().length > 0;
  });
};

const normalizeSmartQuotes = (text) =>
  text
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\uFEFF/g, "");

const stripCodeFences = (text) => {
  if (!text || typeof text !== "string") return "";
  let cleaned = text.trim();
  cleaned = cleaned.replace(/^```(?:json|javascript|js)?\s*/i, "");
  cleaned = cleaned.replace(/\s*```\s*$/i, "");
  const fenceMatch = cleaned.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fenceMatch?.[1]) {
    cleaned = fenceMatch[1].trim();
  }
  return cleaned.trim();
};

const extractJsonObjectString = (text) => {
  if (!text || typeof text !== "string") return null;
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) return null;
  return text.slice(start, end + 1);
};

const removeTrailingCommas = (text) =>
  text.replace(/,\s*([}\]])/g, "$1");

/**
 * Escape raw newlines/carriage returns that appear inside JSON string literals.
 */
const escapeNewlinesInJsonStrings = (text) => {
  if (!text) return "";
  let result = "";
  let inString = false;
  let escape = false;
  let quoteChar = '"';

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (escape) {
      result += char;
      escape = false;
      continue;
    }

    if (char === "\\") {
      result += char;
      escape = true;
      continue;
    }

    if (!inString && (char === '"' || char === "'")) {
      inString = true;
      quoteChar = char;
      result += char;
      continue;
    }

    if (inString && char === quoteChar) {
      inString = false;
      result += char;
      continue;
    }

    if (inString) {
      if (char === "\n") {
        result += "\\n";
        continue;
      }
      if (char === "\r") {
        result += "\\r";
        continue;
      }
      if (char === "\t") {
        result += "\\t";
        continue;
      }
    }

    result += char;
  }

  return result;
};

const collapseExcessiveWhitespaceOutsideStrings = (text) => {
  if (!text) return "";
  let result = "";
  let inString = false;
  let escape = false;
  let quoteChar = '"';

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (escape) {
      result += char;
      escape = false;
      continue;
    }

    if (char === "\\") {
      result += char;
      escape = true;
      continue;
    }

    if (!inString && (char === '"' || char === "'")) {
      inString = true;
      quoteChar = char;
      result += char;
      continue;
    }

    if (inString && char === quoteChar) {
      inString = false;
      result += char;
      continue;
    }

    if (!inString && (char === "\n" || char === "\r" || char === "\t")) {
      if (result.endsWith(" ")) continue;
      result += " ";
      continue;
    }

    result += char;
  }

  return result.trim();
};

const buildParseCandidates = (text) => {
  const stripped = stripCodeFences(text);
  const extracted = extractJsonObjectString(stripped);
  const normalized = normalizeSmartQuotes(stripped);
  const normalizedExtracted = extracted
    ? normalizeSmartQuotes(extracted)
    : null;

  const variants = [
    stripped,
    extracted,
    normalized,
    normalizedExtracted,
    removeTrailingCommas(stripped),
    removeTrailingCommas(extracted || ""),
    escapeNewlinesInJsonStrings(stripped),
    escapeNewlinesInJsonStrings(extracted || ""),
    removeTrailingCommas(escapeNewlinesInJsonStrings(stripped)),
    removeTrailingCommas(escapeNewlinesInJsonStrings(extracted || "")),
    collapseExcessiveWhitespaceOutsideStrings(stripped),
    collapseExcessiveWhitespaceOutsideStrings(extracted || ""),
  ];

  return [...new Set(variants.filter((v) => typeof v === "string" && v.trim()))];
};

const tryParseJsonObject = (candidate) => {
  const parsed = JSON.parse(candidate);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  return parsed;
};

const unwrapParsedObject = (parsed) => {
  if (hasUsableContentKeys(parsed)) return parsed;
  if (
    parsed?.data &&
    typeof parsed.data === "object" &&
    !Array.isArray(parsed.data) &&
    hasUsableContentKeys(parsed.data)
  ) {
    return parsed.data;
  }
  if (
    parsed?.result &&
    typeof parsed.result === "object" &&
    !Array.isArray(parsed.result) &&
    hasUsableContentKeys(parsed.result)
  ) {
    return parsed.result;
  }
  return parsed;
};

const logParseFailure = (original, cleaned, parseError) => {
  console.error(`${LOG_PREFIX} Failed to parse AI response`, {
    parseError: parseError?.message || String(parseError),
    original: truncateForLog(original),
    cleaned: truncateForLog(cleaned),
  });
};

/**
 * @param {unknown} raw - API result or string
 * @returns {{ success: boolean, data: object|null, error: string|null }}
 */
export const parseAIResponse = (raw) => {
  if (raw === null || raw === undefined) {
    return { success: false, data: null, error: "Empty AI response" };
  }

  if (typeof raw === "object" && !Array.isArray(raw)) {
    const unwrapped = unwrapParsedObject(raw);
    if (hasUsableContentKeys(unwrapped)) {
      return { success: true, data: unwrapped, error: null };
    }
    if (Object.keys(unwrapped).length > 0) {
      return { success: true, data: unwrapped, error: null };
    }
    return {
      success: false,
      data: null,
      error: "AI response did not contain usable content fields",
    };
  }

  if (typeof raw === "string" && raw.trim() === "") {
    return { success: false, data: null, error: "Empty AI response" };
  }

  if (typeof raw !== "string") {
    return {
      success: false,
      data: null,
      error: "Unexpected AI response format",
    };
  }

  const original = raw;
  const candidates = buildParseCandidates(raw);
  let lastError = null;
  let lastCleaned = candidates[0] || stripCodeFences(raw);

  for (const candidate of candidates) {
    lastCleaned = candidate;
    try {
      const parsed = tryParseJsonObject(candidate);
      if (!parsed) continue;

      const data = unwrapParsedObject(parsed);
      if (hasUsableContentKeys(data)) {
        return { success: true, data, error: null };
      }

      if (Object.keys(data).length > 0) {
        return { success: true, data, error: null };
      }
    } catch (error) {
      lastError = error;
    }
  }

  logParseFailure(original, lastCleaned, lastError);

  return {
    success: false,
    data: null,
    error: "Could not parse AI response as JSON. Please try again.",
  };
};

/**
 * Extract mapped AI field object from POST /ai/generate axios response.
 * Returns an object ready for parseAIResponse, or a raw JSON string when needed.
 */
export const getAIResponseData = (response) => {
  const body = response?.data;
  if (body === null || body === undefined) return null;
  if (body.isSuccess === false) return null;

  const mapped = body?.result?.data;
  if (typeof mapped === "string" && mapped.trim()) {
    return mapped;
  }
  if (
    mapped &&
    typeof mapped === "object" &&
    !Array.isArray(mapped) &&
    Object.keys(mapped).length > 0
  ) {
    return mapped;
  }

  const result = body?.result;

  if (result && typeof result === "object" && !Array.isArray(result)) {
    if (hasUsableContentKeys(result)) return result;

    const nested =
      result.data ??
      result.content ??
      result.parsed ??
      result.fields ??
      null;

    if (
      nested &&
      typeof nested === "object" &&
      !Array.isArray(nested) &&
      Object.keys(nested).length > 0
    ) {
      return nested;
    }

    const rawString =
      typeof result.rawResponse === "string"
        ? result.rawResponse
        : typeof result.text === "string"
        ? result.text
        : typeof result.content === "string"
        ? result.content
        : null;

    if (rawString?.trim()) return rawString;
  }

  const topLevelRaw =
    typeof body?.rawResponse === "string"
      ? body.rawResponse
      : typeof result === "string"
      ? result
      : typeof body?.result === "string"
      ? body.result
      : null;

  if (topLevelRaw?.trim()) return topLevelRaw;

  return null;
};

/**
 * @deprecated Prefer getAIResponseData — kept for callers that need legacy fallbacks.
 */
export const extractAIResultFromResponse = (response) => {
  const primary = getAIResponseData(response);
  if (primary) return primary;

  const body = response?.data;
  if (body === null || body === undefined) return null;
  if (body.isSuccess === false) return null;

  const raw =
    body?.result?.rawResponse ?? body?.rawResponse ?? body?.result ?? null;
  if (typeof raw === "string" && raw.trim()) return raw;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
      return raw.data;
    }
    const { rawResponse, inputTokens, outputTokens, ...fieldData } = raw;
    if (Object.keys(fieldData).length > 0) return fieldData;
  }

  return null;
};
